import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import { FormInstance } from 'antd/es/form';
import axios from 'axios';
import { RootState } from 'modules';
import { useRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components/macro';
import EditQuizInfoModal from 'components/learning/quiz/components/EditQuizInfoModal';
import { getUrlQuery } from 'libs/utils';
import {
  TSteps,
  THeaderQuizInfo,
  CQuizStep,
  CQuizStepIndex,
  TCurrentProcess,
  TCurrentProcessProps,
  EQuizProcessName,
} from 'modules/learning';
import { addHeaderInfo, addDescription } from 'modules/learning/createQuiz';
import { currentProcessState, isGradedState } from 'modules/learning/recoilState';
import { TModalState } from 'modules/types';
import { EOperation } from 'enums/base.enum';
import { ENotificationType } from 'enums/base.enum';
import { Steps, Button, Tag } from 'antd';
import { PrimaryColor, SecondaryColor, device, Colors } from 'GlobalStyles';
import { ArrowLeftSquare } from '@styled-icons/bootstrap/ArrowLeftSquare';
import { ArrowRightSquare } from '@styled-icons/bootstrap/ArrowRightSquare';
import { TextBulletListSquare } from '@styled-icons/fluentui-system-regular/TextBulletListSquare';

const { Step } = Steps;

function QuizHeader({ form }: { form: FormInstance }) {
  const location = useLocation();
  const { operation, id }: { operation: string; id: string } = useParams();
  const [editQuizInfoModal, setEditQuizInfoModal] = useState<TModalState>({
    record: '',
    visible: false,
  });
  const [quizInfo, setQuizInfo] = useState<THeaderQuizInfo>();
  const dispatch = useDispatch();
  const quizState = useSelector((store: RootState) => store.createQuizReducer);
  const history = useHistory();

  //초기작성하고 임시저장 했을 경우
  //패스 파라미터 값으로 id가 들어오면서 바뀜
  //get으로 데이터 받아주고 리덕스 setState하면서 quizState.id값이 생김
  //quizInfo를 다시 세팅해주어야 헤더에 step 나타나 주는게 제대로됨
  useEffect(() => {
    //어드민 수정 페이지이거나 사용자 뷰일 때
    if (!!id && Number(id) === quizState.id) {
      setQuizInfo({
        quiz_number: quizState.quiz_number,
        quiz_title: quizState.title,
        quiz_step: {
          step_name: quizState.quiz_type,
          step_index: CQuizStepIndex[quizState.quiz_type],
        },
        quiz_subject: quizState.subject,
        quiz_group_id: quizState.quiz_group_id,
        connected_example_id: quizState.example_id,
      });
    }
  }, [quizState.id]);

  useEffect(() => {
    if (operation !== 'view' && quizInfo) {
      dispatch(addHeaderInfo(quizInfo));
    }
  }, [quizInfo]);

  const moveToPrevQuiz = () => {
    if (quizState.previous_id) {
      history.push({
        pathname: `/case/quiz/view/${quizState.previous_id}`,
      });
    } else {
      history.push({
        pathname: `/case/quiz/view/${quizState.id}`,
        state: {
          notification: {
            type: ENotificationType.Error,
            message: '이전 문제가 없습니다',
          },
        },
      });
    }
  };

  const moveToNextQuiz = () => {
    if (quizState.next_id) {
      history.push({
        pathname: `/case/quiz/view/${quizState.next_id}`,
      });
    } else {
      history.push({
        pathname: `/case/quiz/view/${quizState.id}`,
        state: {
          notification: {
            type: ENotificationType.Error,
            message: '다음 문제가 없습니다',
          },
        },
      });
    }
  };

  return (
    <QuizHeaderWrapper>
      {operation === EOperation.Create || operation === EOperation.Edit ? (
        <StyledQuizHeader>
          <Tag className='yellow'>
            {quizInfo
              ? quizInfo.quiz_number //모달 닫히고 받은 정보 quizInfo
              : quizState.quiz_number
              ? quizState.quiz_number //quizState.quiz_number 가 0(false)-초기값이 아닐 때
              : `0 #` //아얘 초기 생성창
            }
          </Tag>
          <StyledTitle>
            {quizInfo ? quizInfo.quiz_title : quizState.title.length >= 1 ? quizState.title : `이름 입력 `}
          </StyledTitle>
          <Button
            onClick={() => {
              setEditQuizInfoModal({
                type: 'settingQuizInfo',
                visible: true,
                onFinish: (data: any) => {
                  setQuizInfo(data);
                },
              });
            }}
          >
            문제 기본정보 편집
          </Button>
          {id && !quizState.is_completed && <Button className='danger not-completed'>* 임시 저장</Button>}
          <EditQuizInfoModal modalState={editQuizInfoModal} setModalState={setEditQuizInfoModal} />
        </StyledQuizHeader>
      ) : (
        <StyledQuizHeader>
          {/* 제목 보여줄 수 있는 폼넣기 name={quiz_title} */}
          <StyledNumberTitle>
            <Tag className='yellow'>{quizInfo?.quiz_number}</Tag>
            <StyledTitle>{quizInfo?.quiz_title}</StyledTitle>
          </StyledNumberTitle>

          <StyledMoveIconButtons>
            <LeftSquareArrow fill={Colors.white} onClick={moveToPrevQuiz} />
            <RightSquareArrow fill={Colors.white} onClick={moveToNextQuiz} />
            <ListIcon fill={Colors.white} onClick={() => history.push('/case/quizzes')} />
          </StyledMoveIconButtons>
        </StyledQuizHeader>
      )}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {operation === 'view' && (
          <StyledMoveButtons>
            <Button className='ant-btn-primary-white-border' onClick={moveToPrevQuiz}>
              이전
            </Button>
            <Button onClick={moveToNextQuiz}>다음</Button>
            <Button className='ant-btn-primary-white-border' onClick={() => history.push('/case/quizzes')}>
              목록
            </Button>
          </StyledMoveButtons>
        )}

        <QuizStep form={form} activeStep={quizInfo?.quiz_step} />
      </div>
    </QuizHeaderWrapper>
  );
}

function QuizStep({
  form,
  activeStep, //해당 문제가 가지는 step에 대한 배열
}: {
  form: FormInstance;
  activeStep: any;
}) {
  const history = useHistory();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const { step, step_name, answer_type } = getUrlQuery(location.search);
  const { operation, id }: { operation: string; id: string } = useParams();
  const [steps, setSteps] = useState<Array<TSteps>>();
  const quizState = useSelector((store: RootState) => store.createQuizReducer);
  const [currentProcess, setCurrentProcess] = useRecoilState<TCurrentProcess>(currentProcessState);
  const [fullDrillStepArray, setFullDrillStepArray] = useState<Array<string>>();

  useEffect(() => {
    const quizStepData = async () => {
      const { data } = await axios.get('/data/quizStepData.json');
      setSteps(data);
    };
    quizStepData();
  }, []);

  function onChange(target: number) {
    const fieldErrorList = form.getFieldsError();
    let hasError = false;

    if (hasError == false) {
      setCurrentStep(target);
    }
  }

  const currentStepName = (current: any) => {
    const name = steps?.find((el: TSteps) => el.step_index === current)?.step_name;

    return name;
  };

  useEffect(() => {
    if (operation === 'view') {
    } else {
      //⭐️ 리덕스 실행 후 url 바꿔주기

      //id 값이 있으면 ==> 수정
      if (id) {
        history.push(
          `/case/quiz/create/${id}?step=${currentStep}&step_name=${currentStepName(
            currentStep,
          )}&answer_type=${answer_type}`,
        );
      } else {
        //id 값이 없으면 ==> 초기 작성
        history.push(
          `/case/quiz/create?step=${currentStep}&step_name=${currentStepName(
            currentStep,
          )}&answer_type=${answer_type}`,
        );
      }
    }
  }, [currentStep, steps]);

  useEffect(() => {
    if (activeStep) {
      setCurrentStep(activeStep.step_index);
    }
  }, [activeStep]);

  //steps 데이터 받음
  //steps로 <step> antd 태그로 렌더링시켜줌
  //생성모드 >>> 모달에서 받아준 생성할 step 정보에 대해 header컴포넌트에서 activeStep={quizInfo?.quiz_step}로 프롭스 받아줌
  //==> 받아준 activeStep 의 [0] 값으로 currentStep 바꿔주면서 현재 step 색깔 활성화

  const makeFullDrillStepArray = () => {
    //quizState.full_drill에 객체 값이 들어올 경우 (null이 아닐 경우 , 풀드릴 문제일 경우)
    //true인 key 모두 뽑아서 배열
    if (quizState.full_drill) {
      const fullDrillArr = Object.keys(quizState.full_drill).filter((el: string) => {
        if (quizState.full_drill) return quizState?.full_drill[el] === true;
      });
      return fullDrillArr;
    }
  };

  useEffect(() => {
    if (quizState.full_drill) {
      setFullDrillStepArray(makeFullDrillStepArray());
    }
  }, [quizState.full_drill]);

  useEffect(() => {
    setFullDrillStepArray([]);
  }, [id]);

  return (
    <QuizStepWrapper
      id={`${EQuizProcessName.QuizStep}_popper_reference`}
      currentProcess={currentProcess.name === EQuizProcessName.QuizStep}
    >
      <Steps
        className='steps-wrapper'
        current={currentStep}
        onChange={onChange}
        type='navigation'
        size='small'
      >
        {activeStep &&
          // activeStep && //문제 생성 모드에서 disabled 적용해준거 같이 사용자 뷰모드에서 데이터 있는 문제 step만 활성화되게 하려면 activeStep값을 뷰모드에서도 받아줄 수 있도록
          steps?.map((step) => (
            <Step
              key={step.step_name}
              title={CQuizStep[step.step_name]} //이름
              status={currentStep === step.step_index ? 'process' : 'wait'}
              disabled={
                quizState.full_drill && fullDrillStepArray?.includes(step.step_name)
                  ? false
                  : activeStep.step_index === step.step_index
                  ? false
                  : true
              }
              onClick={() => {
                if (quizState.full_drill) {
                  const stepId = quizState.full_drill[`${step.step_name}_id`];
                  stepId && history.push(`/case/quiz/${operation}/${stepId}`);
                }
              }}
            />
          ))}
      </Steps>
    </QuizStepWrapper>
  );
}

export default QuizHeader;

const QuizStepWrapper = styled.div<TCurrentProcessProps>`
  padding: 20px 10px 20px 40px;
  //💜 도움말에서 해당 파트일 때
  ${(props) => props.currentProcess && 'max-width: 590px;'}
  ${(props) => props.currentProcess && 'max-height: 100%;'}
  ${(props) => props.currentProcess && 'z-index: 90;'}
  position: relative;
  background-color: ${PrimaryColor};
  @media screen and ${device.sm} {
    ${(props) => props.currentProcess && 'z-index: unset;'}
    ${(props) => props.currentProcess && 'max-height: 160px;'}
  }

  /* margin-right: 20px; */
  & .ant-steps {
    margin-right: 0;
    @media screen and ${device.lg} {
      display: inline-block;
      ${(props) => props.currentProcess && 'max-width:100%;'}
    }
  }

  //번호 원
  & .ant-steps-small .ant-steps-item-icon {
    width: 20px;
    height: 20px;
    line-height: 21px;
    border-radius: 20px;
    border: 0;
  }

  & .ant-steps-small .ant-steps-item-title {
    font-size: 13px;
    line-height: 17px;
  }

  & .ant-steps-navigation .ant-steps-item {
    margin: 0 20px;
  }

  & .ant-steps-navigation .ant-steps-item:nth-child(2) {
    margin: 0 35px 0 20px;
  }

  & .ant-steps-navigation .ant-steps-item::before {
    background-color: transparent;
  }

  //선택되지 않은 번포 배경
  & .ant-steps-item-wait .ant-steps-item-icon {
    background-color: ${Colors.grey2};
  }
  //선택되지 않은 번호의 글자 컬러
  & .ant-steps-item-wait .ant-steps-item-icon > .ant-steps-icon {
    color: ${PrimaryColor};
  }

  & .ant-steps-item-container {
    font-weight: 700;
  }
  //선택한 번호
  & .ant-steps-item-process > .ant-steps-item-container > .ant-steps-item-icon {
    background-color: ${SecondaryColor};
  }
  //선택한 번호의 글자 컬러
  &
    .ant-steps-item-process
    > .ant-steps-item-container
    > .ant-steps-item-icon
    .ant-steps-icon {
    color: ${PrimaryColor};
  }

  //선택한 타이틀
  &
    .ant-steps-item-process
    > .ant-steps-item-container
    > .ant-steps-item-content
    > .ant-steps-item-title {
    color: ${SecondaryColor};
  }

  //호버했을 때
  &
    .ant-steps
    .ant-steps-item:not(.ant-steps-item-active)
    > .ant-steps-item-container[role='button']:hover
    .ant-steps-item-title {
    color: #83979f;
  }
  //호버 안했을 때 선택안한 글시
  &
    .ant-steps-item-wait
    > .ant-steps-item-container
    > .ant-steps-item-content
    > .ant-steps-item-title {
    color: #83979f;
  }

  & .ant-steps-navigation .ant-steps-item::after {
    width: 10px;
    height: 10px;
    margin-top: -10px;
    border: 2px solid #83979f;
    border-bottom: none;
    border-left: none;
    left: 110%;
  }

  & .ant-steps-navigation.ant-steps-small .ant-steps-item-container {
    margin-left: -21px;
    display: flex;
    align-items: center;
  }

  &
    .ant-steps-small.ant-steps-horizontal:not(.ant-steps-label-vertical)
    .ant-steps-item
    :nth-child(3) {
    padding-left: 0px;
  }
`;

const QuizHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 20px;
  border: 1px solid black;
  background-color: ${PrimaryColor};
  @media screen and ${device.lg} {
    flex-direction: column;
    align-items: unset;
  }

  & .ant-btn.not-completed {
    border-color: transparent !important;
    margin-left: 10px;
    font-size: 11px;
    height: unset;
    padding: 0 5px !important;
    border-radius: 6px !important;
  }
`;

const StyledQuizHeader = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  /* justify-content: center; */
  @media screen and ${device.lg} {
    align-items: center;
    justify-content: space-between;
  }
`;

const StyledNumberTitle = styled.div`
  display: flex;
  align-items: center;
  @media screen and ${device.lg} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StyledTitle = styled.div`
  font-size: 1.5em;
  color: white;
  margin-bottom: 0;
  padding: 0 15px;
  @media screen and ${device.lg} {
    padding: 0;
  }
`;

const StyledMoveButtons = styled.div`
  & .ant-btn {
    margin-right: 10px;
  }
  display: flex;

  @media screen and ${device.lg} {
    display: none;
  }
`;

const StyledMoveIconButtons = styled.div`
  display: none;
  @media screen and ${device.lg} {
    display: flex;
  }
`;

const LeftSquareArrow = styled(ArrowLeftSquare)`
  width: 2.5rem;
  margin-right: 11px;
`;
const RightSquareArrow = styled(ArrowRightSquare)`
  width: 2.5rem;
  margin-right: 5px;
`;
const ListIcon = styled(TextBulletListSquare)`
  width: 3.2rem;
  color: ${Colors.white};
`;
